import { toast } from '@rentnerkev/toasts'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { loginHandler } from '@/features/Auth/Login/server.ts'
import type {
    LoginFormValues,
    LoginRedirectReason,
    LoginRequestValues,
} from '@/features/Auth/Login/Types/types.ts'
import useTranslationStore from '@/language/useTranslationStore'
import { AppError, toastAppError } from '@/lib/Errors/AppError.ts'

export default function useLoginLogic(redirectReason?: LoginRedirectReason) {
    const navigate = useNavigate()
    const { t } = useTranslationStore()
    const [requiresTwoFactor, setRequiresTwoFactor] = useState(false)
    const [twoFactorCode, setTwoFactorCode] = useState<Array<string>>([
        '',
        '',
        '',
        '',
        '',
        '',
    ])
    const [trustThisDevice, setTrustThisDevice] = useState(false)
    const [isTwoFactorError, setIsTwoFactorError] = useState(false)
    const twoFactorInput = useRef<HTMLInputElement>(null)
    const hasShownRedirectToast = useRef(false)

    useEffect(() => {
        if (!redirectReason || hasShownRedirectToast.current) {
            return
        }

        hasShownRedirectToast.current = true
        const isMissingToken = redirectReason === 'password-reset-token-missing'

        toast.error(
            t(
                isMissingToken
                    ? 'auth.passwordReset.missingLinkDescription'
                    : 'auth.passwordReset.invalidLinkDescription',
            ),
            {
                title: t(
                    isMissingToken
                        ? 'auth.passwordReset.missingLinkTitle'
                        : 'auth.passwordReset.invalidLinkTitle',
                ),
            },
        )

        void navigate({
            to: '/login',
            hash: '',
            replace: true,
        })
    }, [navigate, redirectReason, t])

    const form = useForm({
        defaultValues: {
            username: '',
            password: '',
        } satisfies LoginFormValues,
        onSubmit: async ({ value }) => {
            loginMutation(value)
        },
    })

    const { mutate: loginMutation, isPending: isLoading } = useMutation({
        mutationFn: async function (formData: LoginRequestValues) {
            return await loginHandler({
                data: {
                    username: formData.username,
                    password: formData.password,
                    twoFactorCode: formData.twoFactorCode,
                    trustThisDevice: formData.trustThisDevice,
                },
            })
        },
        onSuccess: async function (result) {
            if (result.success) {
                toast.success(t('toast.loginSuccess'), {
                    title: t('auth.login.title'),
                })

                if (result.loggedOutDueToMissingPermissions) {
                    toast.error(
                        result.error || t('toast.loginMissingPermissions'),
                        { title: t('common.error') },
                    )
                }

                await navigate({ to: result.redirectTo })
                return
            }

            if (result.requiresTwoFactor) {
                setRequiresTwoFactor(true)
                setIsTwoFactorError(
                    Boolean(
                        result.error && twoFactorCode.join('').length === 6,
                    ),
                )
                setTimeout(() => twoFactorInput.current?.focus(), 0)
                toast.error(
                    result.error || t('auth.login.twoFactorCodeRequired'),
                    { title: t('common.error') },
                )
                return
            }

            toastAppError(new AppError(result.error || t('auth.login.failed')))
        },
        onError: function (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : t('auth.login.failed'),
                { title: t('common.error') },
            )
        },
    })

    function handleTwoFactorSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        event.stopPropagation()
        const code = twoFactorCode.join('')

        if (code.length !== 6) {
            setIsTwoFactorError(true)
            toast.error(t('auth.login.twoFactorCodeIncomplete'), {
                title: t('common.error'),
            })
            return
        }

        loginMutation({
            username: form.state.values.username,
            password: form.state.values.password,
            twoFactorCode: code,
            trustThisDevice,
        })
    }

    function handleTwoFactorChange(value: Array<string>) {
        setIsTwoFactorError(false)
        setTwoFactorCode(value)
    }

    function handleBackToLogin() {
        setRequiresTwoFactor(false)
        setTwoFactorCode(['', '', '', '', '', ''])
        setTrustThisDevice(false)
        setIsTwoFactorError(false)
    }

    return {
        form,
        state: {
            isLoading,
            requiresTwoFactor,
            twoFactorCode,
            trustThisDevice,
            isTwoFactorError,
        },
        handler: {
            handleTwoFactorSubmit,
            handleTwoFactorChange,
            handleBackToLogin,
        },
        setter: {
            setTrustThisDevice,
        },
        refs: {
            twoFactorInput,
        },
    }
}
